import React, { useState, useRef, useEffect } from 'react';
import { X, MapPin, Send, Loader2, Camera, Info, Tent, Image as ImageIcon, Trash2, Save, ShieldCheck, MailQuestion, Star, Link as LinkIcon, Navigation, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { Coordinates, CampingSpot, AppUser } from '../types';
import { auth, suggestCategoryChange, addReview, aiAnalyzeSpotImage, aiSuggestSpotFromContext, aiEnrichCommercialCampsite } from '../services/firebaseService';
import { mergeCampsiteEnrichment } from '../services/campsiteAutofill';
import { fetchNearestBuildingDist } from '../services/overpassService';
import { isAdminUser } from '../services/accessControl';
import { NON_DESCRIPTIVE_CATEGORY_IDS } from '../services/spotIdentity';
import { TIER_PRIVILEGES } from '../constants';
import { useTranslation } from 'react-i18next';
import { readExifGps, approxDistanceMeters } from '../utils/exifGps';
import { useToast } from '../contexts/ToastContext';
import { Capacitor } from '@capacitor/core';
import { pickPhoto } from '../services/cameraService';
import { compressImages, compressImagesDetailed } from '../utils/imageCompression';
import { emitRewardEvent } from '../services/rewardEvents';

import { BRAND } from '../constants/mapColors';
export interface AddSpotSubmitOptions {
  shareWithCommunity: boolean;
  saveToMyContent: boolean;
}

interface AddSpotModalProps {
  coords: Coordinates;
  onClose: () => void;
  onSubmit: (data: Partial<CampingSpot>, files: File[], options?: AddSpotSubmitOptions) => Promise<void>;
  initialData?: CampingSpot;
  user?: AppUser | null;
  initialFiles?: File[];
}

interface SpotCategory {
  id: string;
  label: string;
  feature: string;
  icon: React.ReactNode;
  color: string;
  spotType: 'confirmed' | 'osm_amenity' | 'rasteplass' | 'coastal' | 'attraction' | 'commercial_campsite' | 'forbidden_area';
}

const getCategories = (t: any): SpotCategory[] => [
  { id: 'fricamp', label: t('add_spot.categories.fricamp'), feature: 'FRICAMP', icon: <i className="fa-solid fa-tent text-lg" />, color: 'bg-emerald-500', spotType: 'confirmed' },
  { id: 'teltplass', label: t('add_spot.categories.teltplass'), feature: 'TELTPLASS', icon: <i className="fa-solid fa-tent text-lg" />, color: 'bg-emerald-700', spotType: 'confirmed' },
  { id: 'commercial_campsite', label: t('add_spot.categories.commercial_campsite', 'Campingplass'), feature: 'KOMMERSIELL_CAMPING', icon: <i className="fa-solid fa-caravan text-lg" />, color: 'bg-amber-500', spotType: 'commercial_campsite' },
  { id: 'gapahuk', label: t('add_spot.categories.gapahuk'), feature: 'GAPAHUK', icon: <i className="fa-solid fa-person-shelter text-lg" />, color: 'bg-emerald-600', spotType: 'osm_amenity' },
  { id: 'utsikt', label: t('add_spot.categories.utsikt'), feature: 'UTSIKT', icon: <i className="fa-solid fa-mountain text-lg" />, color: 'bg-emerald-500', spotType: 'osm_amenity' },
  { id: 'balplass', label: t('add_spot.categories.balplass'), feature: 'BÅLPLASS', icon: <i className="fa-solid fa-fire text-lg" />, color: 'bg-orange-500', spotType: 'osm_amenity' },
  { id: 'toalett', label: t('add_spot.categories.toalett'), feature: 'TOALETT', icon: <i className="fa-solid fa-restroom text-lg" />, color: 'bg-blue-600', spotType: 'osm_amenity' },
  { id: 'badeplass', label: t('add_spot.categories.badeplass'), feature: 'BADEPLASS', icon: <i className="fa-solid fa-person-swimming text-lg" />, color: 'bg-cyan-500', spotType: 'osm_amenity' },
  { id: 'vann', label: t('add_spot.categories.vann'), feature: 'VANN', icon: <i className="fa-solid fa-droplet text-lg" />, color: 'bg-blue-400', spotType: 'osm_amenity' },
  { id: 'picnic', label: t('add_spot.categories.picnic'), feature: 'PICNIC', icon: <i className="fa-solid fa-utensils text-lg" />, color: 'bg-blue-500', spotType: 'osm_amenity' },
  { id: 'kai', label: t('add_spot.categories.kai'), feature: 'KAI/BRYGGE', icon: <i className="fa-solid fa-anchor text-lg" />, color: 'bg-blue-700', spotType: 'coastal' },
  { id: 'bom', label: t('add_spot.categories.bom'), feature: 'BOM', icon: <i className="fa-solid fa-road-barrier text-lg" />, color: 'bg-red-600', spotType: 'confirmed' },
  { id: 'camping_forbudt', label: t('add_spot.categories.camping_forbudt', 'Camping forbudt'), feature: 'CAMPING_FORBUDT', icon: <i className="fa-solid fa-ban text-lg" />, color: 'bg-red-700', spotType: 'forbidden_area' },
  { id: 'attraksjon', label: t('add_spot.categories.attraksjon', 'Attraksjon'), feature: 'ATTRAKSJON', icon: <i className="fa-solid fa-landmark text-lg" />, color: 'bg-violet-500', spotType: 'attraction' },
];

// Per-user category usage tracking for smart sorting.
const USAGE_KEY = 'fricamp_spot_category_usage';
type CategoryUsage = Record<string, number>;
const getCategoryUsage = (): CategoryUsage => {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(USAGE_KEY) : null;
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};
const bumpCategoryUsage = (id: string) => {
  try {
    const u = getCategoryUsage();
    u[id] = (u[id] || 0) + 1;
    localStorage.setItem(USAGE_KEY, JSON.stringify(u));
  } catch { /* ignore */ }
};
// Sort categories by usage count desc, tie-break preserves PRIMARY then original order.
const sortCategoriesByUsage = (cats: SpotCategory[], usage: CategoryUsage): SpotCategory[] => {
  return [...cats].sort((a, b) => {
    const ua = usage[a.id] || 0;
    const ub = usage[b.id] || 0;
    if (ua !== ub) return ub - ua;
    const aPrimary = PRIMARY_CATEGORY_IDS.indexOf(a.id);
    const bPrimary = PRIMARY_CATEGORY_IDS.indexOf(b.id);
    if (aPrimary !== bPrimary) {
      if (aPrimary === -1) return 1;
      if (bPrimary === -1) return -1;
      return aPrimary - bPrimary;
    }
    return 0;
  });
};
const PRIMARY_COUNT = 6;

const SATELLITE_TILE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';

const PRIMARY_CATEGORY_IDS = ['fricamp', 'teltplass', 'gapahuk', 'balplass', 'badeplass'];

const exifPinIcon = L.divIcon({
  className: '',
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  html: '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="#ef4444" stroke="white" stroke-width="1.5"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3" fill="white"/></svg>',
});

// Helper function to check if a string is a valid URL
function isValidHttpUrl(string: string): boolean {
  try {
    const url = new URL(string);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function getVideoThumbnail(url: string): string | null {
  try {
    const u = new URL(url);
    // YouTube
    const ytMatch = u.hostname.match(/(?:youtube\.com|youtu\.be)/);
    if (ytMatch) {
      let videoId: string | null = null;
      if (u.hostname === 'youtu.be') videoId = u.pathname.slice(1);
      else videoId = u.searchParams.get('v');
      if (videoId) return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    }
    // Vimeo
    if (u.hostname.includes('vimeo.com')) {
      const match = u.pathname.match(/\/(\d+)/);
      if (match) return `https://vumbnail.com/${match[1]}.jpg`;
    }
  } catch { /* ignore */ }
  return null;
}

/** Målt bredde/høyde etter nedskalering til `maxSize` på lengste kant. */
export const fitWithin = (width: number, height: number, maxSize: number): { width: number; height: number } => {
  const longest = Math.max(width, height);
  if (longest <= maxSize) return { width, height };
  const scale = maxSize / longest;
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
};

/**
 * Skaler et bilde ned og returner base64 (uten data:-prefiks) for AI-analyse.
 *
 * Gikk tidligere via `new Image()` + `drawImage` + `canvas.toDataURL`. Det dekoder
 * bildet i FULL oppløsning (12–50 MP fra et telefonkamera) før nedskaleringen, og
 * `toDataURL` er en synkron encode på main thread. To lange frys per bilde, midt i
 * legg-til-plass-flyten.
 *
 * `createImageBitmap` med `resizeWidth`/`resizeHeight` gjør nedskaleringen som del
 * av dekodingen, utenfor main thread, så vi aldri holder hele fullbildet i minnet.
 * `canvas.convertToBlob` (OffscreenCanvas) er også async. Faller tilbake til den
 * gamle stien der API-ene mangler.
 */
const resizeImageToBase64 = async (file: File, maxSize: number): Promise<string> => {
  if (typeof createImageBitmap === 'function' && typeof OffscreenCanvas === 'function') {
    let bitmap: ImageBitmap | undefined;
    try {
      // Første dekoding kun for målene — billig, `resizeWidth` krever kjent forhold.
      const probe = await createImageBitmap(file);
      const { width, height } = fitWithin(probe.width, probe.height, maxSize);
      probe.close?.();
      bitmap = await createImageBitmap(file, {
        resizeWidth: width,
        resizeHeight: height,
        resizeQuality: 'medium',
      });
      const canvas = new OffscreenCanvas(width, height);
      canvas.getContext('2d')!.drawImage(bitmap, 0, 0);
      const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.7 });
      const buffer = await blob.arrayBuffer();
      let binary = '';
      const bytes = new Uint8Array(buffer);
      const CHUNK = 0x8000; // unngå «too many arguments» på store buffere
      for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
      }
      return btoa(binary);
    } catch (e) {
      console.warn('[resizeImageToBase64] createImageBitmap-sti feilet, faller tilbake:', e);
    } finally {
      bitmap?.close?.();
    }
  }

  // Fallback: gammel WebView uten createImageBitmap/OffscreenCanvas.
  return new Promise<string>((resolve, reject) => {
    const img = new Image();
    const objUrl = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const { width, height } = fitWithin(img.width, img.height, maxSize);
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
      const dataUrl = canvas.toDataURL(file.type || 'image/jpeg', 0.7);
      URL.revokeObjectURL(objUrl);
      resolve(dataUrl.split(',')[1]);
    };
    img.onerror = (e) => { URL.revokeObjectURL(objUrl); reject(e); };
    img.src = objUrl;
  });
};

const ExifMapInvalidator: React.FC = () => {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 150);
    return () => clearTimeout(t);
  }, [map]);
  return null;
};

// New internal component to track map center moves
const MapCenterTracker: React.FC<{ onCenterChange: (c: Coordinates) => void }> = ({ onCenterChange }) => {
  useMapEvents({
    moveend: (e: any) => {
      const center = e.target.getCenter();
      onCenterChange({ latitude: center.lat, longitude: center.lng });
    }
  });
  return null;
};

const AddSpotModal: React.FC<AddSpotModalProps> = ({ coords, onClose, onSubmit, initialData, user = null, initialFiles }) => {
  const { t } = useTranslation();
  const toast = useToast();
  // Snapshot usage on open so ordering stays stable while the modal is open.
  const categoryUsageRef = useRef(getCategoryUsage());
  const categories = sortCategoriesByUsage(getCategories(t), categoryUsageRef.current);

  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>(() => {
    if (initialData) {
      const idsFromFeatures = initialData.features
        ? categories.filter(c => initialData.features.includes(c.feature)).map(c => c.id)
        : [];
      if (initialData.spotType === 'commercial_campsite' && !idsFromFeatures.includes('commercial_campsite')) {
        idsFromFeatures.unshift('commercial_campsite');
      }
      if (idsFromFeatures.length > 0) return idsFromFeatures;
      return ['fricamp'];
    }
    return ['fricamp'];
  });
  const selectedCategoryId = selectedCategoryIds[0] || 'fricamp';
  const selectedCategories = categories.filter(c => selectedCategoryIds.includes(c.id));
  // Bom/forbudt-sone/attraksjon er ikke campingplasser — en AI-generert
  // «flott fricamp-plass»-beskrivelse blir feilaktig. Skjul AI-forslaget for slike.
  const allowAiSuggestion = !selectedCategoryIds.some(id => NON_DESCRIPTIVE_CATEGORY_IDS.includes(id));

  const [selectedAdditionalFeatures, setSelectedAdditionalFeatures] = useState<string[]>(() => {
    if (initialData?.features) {
      const categoryFeatures = new Set(categories.map(c => c.feature));
      return initialData.features.filter(f => f !== 'BRUKERBIDRAG' && !categoryFeatures.has(f));
    }
    return [];
  });

  // Show first PRIMARY_COUNT categories (after usage-sort) by default; expand to show the rest.
  const visibleByDefaultIds = categories.slice(0, PRIMARY_COUNT).map(c => c.id);
  const isSecondaryCategory = !visibleByDefaultIds.includes(selectedCategoryId);
  const [showAllCategories, setShowAllCategories] = useState(!!initialData && isSecondaryCategory);

  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>(initialData?.photoUrls || []);
  const [verificationRating, setVerificationRating] = useState(5);
  const [videoUrl, setVideoUrl] = useState(initialData?.videoUrl || '');
  const [website, setWebsite] = useState(initialData?.website || initialData?.commercialMetadata?.website || '');
  // Edit-modus: behold opprinnelig synlighet. Ny-modus styres av
  // `shareWithCommunity`-toggelen lenger ned (single source of truth).
  const isPrivate = initialData?.visibility === 'private';

  const authUser = auth.currentUser;
  const isAdmin = isAdminUser(user);
  const isAdminEditor = isAdmin;
  const isCampingSpot = selectedCategoryId === 'commercial_campsite';
  
  const [showLinks, setShowLinks] = useState(
    !!(initialData?.website || initialData?.videoUrl || initialData?.commercialMetadata?.website) ||
    (isAdminEditor && isCampingSpot)
  );
  const [barrierType, setBarrierType] = useState(initialData?.barrierMetadata?.barrierType || '');
  const [barrierOpeningMethod, setBarrierOpeningMethod] = useState(initialData?.barrierMetadata?.openingMethod || '');
  const [barrierOwner, setBarrierOwner] = useState(initialData?.barrierMetadata?.owner || '');
  const [showModalGuide, setShowModalGuide] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [exifCoords, setExifCoords] = useState<Coordinates | null>(null);
  const [adjustedCoords, setAdjustedCoords] = useState<Coordinates | null>(null);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiSuggested, setAiSuggested] = useState(false);
  const [aiContextLoading, setAiContextLoading] = useState(false);
  const [attractionSubCategory, setAttractionSubCategory] = useState(initialData?.attractionCategory || '');

  // Commercial campsite metadata
  const [campsitePhone, setCampsitePhone] = useState(initialData?.commercialMetadata?.phone || '');
  const [campsiteFee, setCampsiteFee] = useState(initialData?.commercialMetadata?.fee || '');
  const [campsiteOpeningHours, setCampsiteOpeningHours] = useState(initialData?.commercialMetadata?.opening_hours || '');
  // Google Places-autofyll: laster + hvilke felt AI foreslo (for «AI-forslag»-merking).
  // Merkingen fjernes så snart brukeren redigerer feltet — da eier brukeren verdien.
  const [campsiteEnrichLoading, setCampsiteEnrichLoading] = useState(false);
  const [campsiteAiFields, setCampsiteAiFields] = useState<Set<string>>(new Set());
  // Siste navn vi auto-slo opp (blur kan fyre gjentatte ganger på samme navn).
  const autoTriedNameRef = useRef<string>('');
  const clearAiField = (field: string) => setCampsiteAiFields(prev => {
    if (!prev.has(field)) return prev;
    const next = new Set(prev);
    next.delete(field);
    return next;
  });

  // Sharing toggles (kun synlig i ny-modus). Default ON: del med fellesskapet
  // og lagre kopi i Mitt innhold. Brukeren kan slå av deling for å lage et privat
  // waypoint, eller slå av Mitt innhold-lagring hvis de ikke vil ha det i sin liste.
  const [shareWithCommunity, setShareWithCommunity] = useState(true);
  const [saveToMyContent, setSaveToMyContent] = useState(true);
  // Nærmeste bygning i meter — null = ikke målt, undefined = laster
  const [nearestBuildingM, setNearestBuildingM] = useState<number | null | undefined>(null);

  const trimmedVideoUrl = videoUrl.trim();
  const hasVideoUrl = trimmedVideoUrl.length > 0;
  const isVideoUrlValid = !hasVideoUrl || isValidHttpUrl(trimmedVideoUrl);
  const videoThumbnail = hasVideoUrl && isVideoUrlValid ? getVideoThumbnail(trimmedVideoUrl) : null;

  const trimmedWebsite = website.trim();
  const hasWebsite = trimmedWebsite.length > 0;
  const isWebsiteValid = !hasWebsite || isValidHttpUrl(trimmedWebsite);

  const isEditMode = !!initialData;
  const isAiPoint = initialData?.spotType === 'algorithmic';
  const hasNewPhotos = selectedFiles.length > 0;
  const isNonOwner = isEditMode && authUser?.uid !== initialData?.createdBy && !isAdmin;
  const canVerifyExistingSpot = isNonOwner;
  const isSuggestionMode = false;
  const willVerify = isEditMode ? (hasNewPhotos || isNonOwner) : hasNewPhotos;
  const shouldLockSpotMetadata = false;
  const showSharingToggles = !isEditMode && !isSuggestionMode;
  const onlyPrivateWaypoint = showSharingToggles && !shareWithCommunity;

  useEffect(() => {
    if (isEditMode) return;
    const key = `fricamp_addspot_guide_v1_${authUser?.uid || 'anon'}`;
    const dismissed = window.localStorage.getItem(key) === '1';
    setShowModalGuide(!dismissed);
  }, [isEditMode, authUser?.uid]);

  // Mål nærmeste bygning når brukeren har valgt å dele. Debouncet ved kart-pan.
  // Sperrer ikke lagringen — bare informativ advarsel hvis < 150m.
  useEffect(() => {
    if (!showSharingToggles || !shareWithCommunity) {
      setNearestBuildingM(null);
      return;
    }
    const target = adjustedCoords || coords;
    if (!target) return;
    setNearestBuildingM(undefined); // loading
    const handle = setTimeout(async () => {
      try {
        const dist = await fetchNearestBuildingDist(target);
        setNearestBuildingM(dist);
      } catch {
        setNearestBuildingM(null);
      }
    }, 600);
    return () => clearTimeout(handle);
  }, [shareWithCommunity, showSharingToggles, adjustedCoords?.latitude, adjustedCoords?.longitude, coords?.latitude, coords?.longitude]);

  const dismissModalGuide = () => {
    const key = `fricamp_addspot_guide_v1_${authUser?.uid || 'anon'}`;
    window.localStorage.setItem(key, '1');
    setShowModalGuide(false);
  };

  useEffect(() => {
    if (!initialFiles || initialFiles.length === 0) return;

    (async () => {
      for (const file of initialFiles) {
        try {
          const c = await readExifGps(file);
          if (c) {
            if (isEditMode) {
              // Don't overwrite an existing spot's location with the photo's GPS.
              // Just notify the user if the photo was taken somewhere else.
              const existing = initialData?.location;
              if (existing) {
                const distM = approxDistanceMeters(c, existing);
                if (distM > 50) {
                  toast(
                    t('add_spot.photo_coords_differ', 'Bildet har andre GPS-koordinater ({{dist}} m unna). Plassen beholder sin opprinnelige posisjon.', { dist: Math.round(distM) }),
                    'info'
                  );
                }
              }
            } else {
              setExifCoords(c);
              setAdjustedCoords(c);
            }
            break;
          }
        } catch (e) { console.warn('[EXIF modal] Parse error:', e); }
      }
      setSelectedFiles(initialFiles);
      setPreviews(prev => [...prev, ...initialFiles.map(f => URL.createObjectURL(f))]);
      if (initialFiles.length > 0) {
        triggerAiAnalysis(initialFiles[0]);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCategorySelect = (cat: SpotCategory) => {
    const wasPrimary = selectedCategoryId === cat.id;
    const wasSelected = selectedCategoryIds.includes(cat.id);
    const nextIds = wasSelected
      ? (selectedCategoryIds.length <= 1 ? selectedCategoryIds : selectedCategoryIds.filter(id => id !== cat.id))
      : [...selectedCategoryIds, cat.id];
    setSelectedCategoryIds(nextIds);

    if (wasSelected && !wasPrimary) return;
    if (!name || name === categories.find(c => c.id === selectedCategoryId)?.label || name.startsWith("Ny ") || name.startsWith("New ")) {
      setName(t('add_spot.new_prefix', { label: cat.label }));
    }
    setSelectedAdditionalFeatures(prev => prev.filter(f => f !== cat.feature));
  };

  const shouldReplaceWithAiSuggestion = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return true;
    const genericNames = categories.flatMap(c => [
      c.label,
      t('add_spot.new_prefix', { label: c.label }),
    ]);
    return genericNames.some(generic => generic.toLowerCase() === trimmed.toLowerCase());
  };

  const triggerContextAiSuggestion = async () => {
    if (aiContextLoading) return;
    const target = adjustedCoords || coords;
    if (!target) return;
    const selectedLabels = selectedCategories.map(c => c.label);
    setAiContextLoading(true);
    try {
      const result = await aiSuggestSpotFromContext({
        coords: target,
        categoryId: selectedCategoryId,
        categoryLabel: selectedLabels[0],
        categoryIds: selectedCategoryIds,
        categoryLabels: selectedLabels,
      });
      if (result?.ok && result.data) {
        const { title, description: desc, context } = result.data;
        if (title) setName(prev => shouldReplaceWithAiSuggestion(prev) ? title : prev);
        if (desc) setDescription(prev => prev.trim() ? prev : desc);
        setAiSuggested(true);
        const ctxParts: string[] = [];
        if (context?.areaLabel) ctxParts.push(context.areaLabel);
        if (context?.elevation != null) ctxParts.push(`${context.elevation} moh`);
        if (context?.surface) ctxParts.push(context.surface);
        context?.nearbyAmenities?.slice(0, 2).forEach(item => {
          ctxParts.push(`${item.label.toLowerCase()} ${item.distanceM} m`);
        });
        const ctxNote = ctxParts.length ? ` (${ctxParts.join(' · ')})` : '';
        toast(t('add_spot.ai_context_filled', { context: ctxNote, defaultValue: `AI foreslo tittel og beskrivelse${ctxNote}` }), 'success');
      } else {
        toast(t('add_spot.ai_context_failed', 'AI-forslag feilet. Prøv igjen.'), 'error');
      }
    } catch (e) {
      console.warn('Context AI suggestion failed:', e);
      toast(t('add_spot.ai_context_failed', 'AI-forslag feilet. Prøv igjen.'), 'error');
    } finally {
      setAiContextLoading(false);
    }
  };

  // Google Places-autofyll for kommersielle campingplasser. Fyller kun TOMME felt
  // (brukerens egne verdier overstyres aldri), og merker de utfylte som AI-forslag.
  //
  // `auto=true` (utløst når brukeren forlater navnefeltet): stille. Fyller BARE
  // ved 'high' konfidens (nær-eksakt navnematch/host-match + nært), og viser ingen
  // feil-toast — vi vil ikke forstyrre brukeren for et gjett.
  // `auto=false` (eksplisitt «Hent info»-klikk): fyller uansett konfidens og gir
  // full tilbakemelding, siden brukeren ba om det.
  const triggerCampsiteEnrichment = async (auto = false) => {
    if (campsiteEnrichLoading) return;
    const target = adjustedCoords || coords;
    const trimmedName = name.trim();
    if (!trimmedName) {
      if (!auto) toast(t('add_spot.campsite.enrich_need_name', 'Skriv inn navnet på campingplassen først.'), 'error');
      return;
    }
    if (!target) return;
    // Dedup for auto: ikke slå opp samme navn på nytt (blur kan fyre gjentatte ganger).
    if (auto) {
      if (trimmedName.length < 3 || autoTriedNameRef.current === trimmedName.toLowerCase()) return;
      autoTriedNameRef.current = trimmedName.toLowerCase();
    }
    setCampsiteEnrichLoading(true);
    try {
      const result = await aiEnrichCommercialCampsite({
        name: trimmedName,
        coords: target,
        website: hasWebsite && isWebsiteValid ? trimmedWebsite : undefined,
      });
      if (result?.ok && result.data) {
        // Auto-fyll krever høy konfidens. Manuell klikk fyller uansett.
        if (auto && result.data.confidence !== 'high') return;
        const { updates, filled } = mergeCampsiteEnrichment(
          { phone: campsitePhone, opening_hours: campsiteOpeningHours, description, website },
          result.data,
        );
        if (updates.phone !== undefined) setCampsitePhone(updates.phone);
        if (updates.opening_hours !== undefined) setCampsiteOpeningHours(updates.opening_hours);
        if (updates.description !== undefined) setDescription(updates.description);
        if (updates.website !== undefined) setWebsite(updates.website);
        setCampsiteAiFields(prev => new Set([...prev, ...filled]));
        if (filled.length > 0) {
          toast(t('add_spot.campsite.enrich_filled', { count: filled.length, defaultValue: `Fylte ut {{count}} felt fra Google. Sjekk og rediger før du lagrer.` }), 'success');
        } else if (!auto) {
          toast(t('add_spot.campsite.enrich_nothing_new', 'Fant plassen, men feltene dine er allerede utfylt.'), 'info');
        }
      } else if (!auto) {
        toast(t('add_spot.campsite.enrich_not_found', 'Fant ikke campingplassen. Fyll ut manuelt.'), 'error');
      }
    } catch (e) {
      console.warn('Campsite enrichment failed:', e);
      if (!auto) toast(t('add_spot.campsite.enrich_failed', 'Autofyll feilet. Prøv igjen eller fyll ut manuelt.'), 'error');
    } finally {
      setCampsiteEnrichLoading(false);
    }
  };

  const triggerAiAnalysis = async (fileOrUrl: File | string) => {
    setAiAnalyzing(true);
    try {
      let base64: string;
      let mimeType = 'image/jpeg';
      if (typeof fileOrUrl === 'string') {
        const resp = await fetch(fileOrUrl);
        const blob = await resp.blob();
        mimeType = blob.type || 'image/jpeg';
        const file = new File([blob], 'photo.jpg', { type: mimeType });
        base64 = await resizeImageToBase64(file, 800);
      } else {
        mimeType = fileOrUrl.type || 'image/jpeg';
        base64 = await resizeImageToBase64(fileOrUrl, 800);
      }
      const result = await aiAnalyzeSpotImage(base64, mimeType);
      if (result?.data) {
        const { category, title, description: desc } = result.data;
        if (title) setName(prev => prev ? prev : title);
        if (desc) setDescription(prev => prev ? prev : desc);
        if (category && categories.some(c => c.id === category)) {
          setSelectedCategoryIds(prev => prev.includes(category) ? prev : [category, ...prev]);
        }
        setAiSuggested(true);
        const parts = [title && t('add_spot.ai_title', 'navn'), desc && t('add_spot.ai_desc', 'beskrivelse'), category && t('add_spot.ai_cat', 'kategori')].filter(Boolean);
        toast(t('add_spot.ai_filled_detail', { parts: parts.join(', '), defaultValue: `AI foreslo: ${parts.join(', ')}` }), 'success');
      }
    } catch (e) {
      console.warn('AI analysis failed:', e);
    } finally {
      setAiAnalyzing(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const originalFiles = Array.from(e.target.files || []) as File[];
    if (originalFiles.length === 0) return;

    if (!exifCoords && !isEditMode) {
      for (const file of originalFiles) {
        try {
          const c = await readExifGps(file);
          if (c) {
            setExifCoords(c);
            setAdjustedCoords(c);
            toast(t('add_spot.exif_coords_found', 'GPS-koordinater funnet i bildet'), 'success');
            break;
          }
        } catch (e) { console.warn('[EXIF handleFile] Parse error:', e); }
      }
    } else if (isEditMode && initialData?.location) {
      // In edit mode, don't change coords — but warn if photo's GPS is far from the spot.
      for (const file of originalFiles) {
        try {
          const c = await readExifGps(file);
          if (c) {
            const existing = initialData.location;
            const distM = approxDistanceMeters(c, existing);
            if (distM > 50) {
              toast(
                t('add_spot.photo_coords_differ', 'Bildet har andre GPS-koordinater ({{dist}} m unna). Plassen beholder sin opprinnelige posisjon.', { dist: Math.round(distM) }),
                'info'
              );
            }
            break;
          }
        } catch (e) { console.warn('[EXIF handleFile] Parse error:', e); }
      }
    }

    const { files: compressedFiles, skipped } = await compressImagesDetailed(originalFiles);
    if (skipped.length > 0) {
      // Kamera-RAW (.dng o.l.) slipper gjennom accept="image/*" på Android, men kan
      // ikke tegnes av noen nettleser. Si fra i stedet for å laste opp et brutt bilde.
      toast(
        t('add_spot.photo_unsupported_format', 'Kan ikke bruke {{files}} — formatet vises ikke i appen. Velg JPG eller PNG.', { files: skipped.join(', ') }),
        'error'
      );
    }
    setSelectedFiles(prev => [...prev, ...compressedFiles]);
    setPreviews(prev => [...prev, ...compressedFiles.map(file => URL.createObjectURL(file))]);

    if (!aiSuggested && !aiAnalyzing && compressedFiles.length > 0) {
      triggerAiAnalysis(compressedFiles[0]);
    }
  };

  const removeFile = (index: number) => {
    const preview = previews[index];
    if (preview.startsWith('blob:')) URL.revokeObjectURL(preview);
    setPreviews(prev => prev.filter((_, i) => i !== index));
    if (preview.startsWith('blob:')) {
      const newFileIndex = previews.slice(0, index).filter(p => p.startsWith('blob:')).length;
      setSelectedFiles(prev => prev.filter((_, i) => i !== newFileIndex));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedDesc = description.trim();
    
    if (!trimmedName || !trimmedDesc || isSubmitting) {
      if (!trimmedName) toast(t('add_spot.alerts.missing_name', 'Vennligst oppgi et navn på plassen'), 'warning');
      else if (!trimmedDesc) toast(t('add_spot.alerts.missing_description', 'Vennligst skriv en kort beskrivelse'), 'warning');
      return;
    }

    const category = categories.find(c => c.id === selectedCategoryId) || categories[0];
    const selectedCategoryFeatures = selectedCategories.map(c => c.feature);
    setIsSubmitting(true);

    try {
      if (isSuggestionMode && initialData && authUser) {
        await suggestCategoryChange(
          initialData.id, 
          authUser.uid, 
          authUser.displayName || "Anonym", 
          initialData.name, 
          name, 
          initialData.description, 
          description, 
          category.spotType, 
          category.color.replace('bg-', '#'), 
          category.feature,
          hasVideoUrl && isVideoUrlValid ? trimmedVideoUrl : undefined,
          hasWebsite && isWebsiteValid ? trimmedWebsite : undefined
        );
        toast(t('add_spot.alerts.suggestion_sent'), 'success');
      } else {
        let features = initialData?.features || [];
        if (!shouldLockSpotMetadata) {
          if (initialData) {
            const allCatFeatures = categories.map(c => c.feature);
            features = features.filter(f => !allCatFeatures.includes(f));
          }
          features = [...new Set(["BRUKERBIDRAG", ...selectedCategoryFeatures, ...selectedAdditionalFeatures, ...features])];
        }

        const hasBarrierCategory = selectedCategoryIds.includes('bom');
        const hasAttractionCategory = selectedCategoryIds.includes('attraksjon');
        const barrierMeta = hasBarrierCategory
          ? { barrierMetadata: { ...(barrierType && { barrierType }), ...(barrierOpeningMethod && { openingMethod: barrierOpeningMethod }), ...(barrierOwner.trim() && { owner: barrierOwner.trim() }) } }
          : (shouldLockSpotMetadata ? { barrierMetadata: initialData?.barrierMetadata } : {});
        const attractionMeta = (hasAttractionCategory && attractionSubCategory)
          ? { attractionCategory: attractionSubCategory }
          : (shouldLockSpotMetadata ? { attractionCategory: initialData?.attractionCategory } : {});

        const commercialMeta = selectedCategoryIds.includes('commercial_campsite')
          ? {
            commercialMetadata: {
              phone: campsitePhone.trim(),
              website: (hasWebsite && isWebsiteValid) ? trimmedWebsite : undefined,
              fee: campsiteFee.trim(),
              opening_hours: campsiteOpeningHours.trim(),
            }
          }
          : (shouldLockSpotMetadata ? { commercialMetadata: initialData?.commercialMetadata } : {});

        const dataToSubmit: Partial<CampingSpot> = {
          ...initialData,
          name: shouldLockSpotMetadata ? initialData?.name : name,
          description: shouldLockSpotMetadata ? initialData?.description : description,
          location: shouldLockSpotMetadata ? initialData?.location : (adjustedCoords || coords),
          photoUrls: previews.filter(p => !p.startsWith('blob:')),
          spotType: (isEditMode && isAdmin) ? initialData?.spotType : (shouldLockSpotMetadata ? initialData?.spotType : category.spotType),
          isVerified: willVerify ? true : initialData?.isVerified,
          features: (isEditMode && isAdmin) ? initialData?.features : features,
          suitabilityColor: (isEditMode && isAdmin) ? initialData?.suitabilityColor : (shouldLockSpotMetadata ? initialData?.suitabilityColor : category.color.replace('bg-', '#')),
          videoUrl: (hasVideoUrl && isVideoUrlValid) ? trimmedVideoUrl : (shouldLockSpotMetadata ? initialData?.videoUrl : undefined),
          website: (hasWebsite && isWebsiteValid && !selectedCategoryIds.includes('commercial_campsite')) ? trimmedWebsite : (shouldLockSpotMetadata ? initialData?.website : undefined),
          // Ny-modus: synlighet utledes av "Del med fellesskapet"-toggelen.
          // Edit-modus: behold opprinnelig synlighet via `isPrivate`.
          visibility: shouldLockSpotMetadata
            ? initialData?.visibility
            : isEditMode
              ? (isPrivate ? 'private' : 'public')
              : (showSharingToggles && !shareWithCommunity ? 'private' : 'public'),
          ...barrierMeta,
          ...attractionMeta,
          ...commercialMeta,
        };

        // For admin edits on commercial campsites, ensure website is always sourced from the input field
        if (isEditMode && isAdmin && selectedCategoryIds.includes('commercial_campsite')) {
          if (dataToSubmit.commercialMetadata) {
            dataToSubmit.commercialMetadata.website = (hasWebsite && isWebsiteValid) ? trimmedWebsite : initialData?.commercialMetadata?.website;
          }
          // Also clear the root website field if it's a campsite to avoid duplication
          delete dataToSubmit.website;
        }

        await onSubmit(dataToSubmit, selectedFiles, showSharingToggles ? { shareWithCommunity, saveToMyContent } : undefined);
        if (!shouldLockSpotMetadata) selectedCategoryIds.forEach(bumpCategoryUsage);

        if (willVerify && isAiPoint && initialData?.id && authUser) {
          await addReview(initialData.id, authUser, verificationRating, description.trim() || t('add_spot.alerts.verified_comment', { name }));
        }
        if (willVerify) {
          emitRewardEvent({
            kind: 'verification',
            title: name,
            message: 'Takk, dette gjør at andre kan stole mer på plassen.',
            rewardPoints: 35,
          });
        }
      }
      if (isSuggestionMode) onClose();
    } catch (error) {
      console.error('[AddSpotModal] handleSubmit failed:', error);
      toast(t('add_spot.alerts.save_error', 'Kunne ikke lagre plassen. Vennligst prøv igjen.'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Guard against silently discarding a half-filled form. Compare current
  // inputs against their initial values so we only prompt when there's real
  // unsaved effort (typed text, added photos, links).
  const isDirty = (
    name !== (initialData?.name || '') ||
    description !== (initialData?.description || '') ||
    selectedFiles.length > 0 ||
    videoUrl !== (initialData?.videoUrl || '') ||
    website !== (initialData?.website || initialData?.commercialMetadata?.website || '')
  );

  const handleClose = () => {
    if (isSubmitting) return;
    if (isDirty && !window.confirm(t('add_spot.discard_confirm', 'Du har ulagrede endringer. Vil du forkaste dem?'))) {
      return;
    }
    onClose();
  };

  // Esc-to-close (respects the dirty guard). The modal previously only closed
  // via backdrop/X — design-review finding C2. Rebinds on the values handleClose
  // reads so the dirty check stays current.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, isSubmitting]);

  return (
    <div className="fixed inset-0 z-[10005] flex items-center justify-center px-4 pb-4" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={handleClose} />
      <div role="dialog" aria-modal="true" aria-label={t('common.add_spot', 'Legg til plass')} className="relative w-full max-w-lg bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-modal shadow-3xl overflow-hidden flex flex-col max-h-[90dvh]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="p-8 overflow-y-auto custom-scrollbar" style={{ overscrollBehavior: 'contain' }}>
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-emerald-500/10 rounded-2xl text-emerald-500">
                  {selectedCategories.slice(0, 3).map((cat) => (
                    <span key={cat.id} className="-ml-1 first:ml-0">{cat.icon}</span>
                  ))}
                  {selectedCategories.length === 0 && <Tent size={24} />}
              </div>
              <div>
                <h2 className="text-xl font-black text-[var(--text-primary)] tracking-tight">
                  {isSuggestionMode ? t('add_spot.title.suggestion') : isEditMode ? t('add_spot.title.edit') : t('add_spot.title.new')}
                </h2>
                <p className="text-3xs text-[var(--text-muted)] font-bold uppercase tracking-widest">
                  {isSuggestionMode ? t('add_spot.subtitle.suggestion') : isEditMode ? t('add_spot.subtitle.edit') : t('add_spot.subtitle.new')}
                </p>
              </div>
            </div>
            <button onClick={handleClose} aria-label={t('common.close', 'Lukk')} className="p-2.5 text-[var(--text-muted)] hover:text-white transition-colors">
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {showModalGuide && !isSuggestionMode && (
              <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/10 p-4 relative">
                <p className="text-2xs text-emerald-100 leading-relaxed pr-8" dangerouslySetInnerHTML={{ __html: t('add_spot.guide') }} />
                <button type="button" onClick={dismissModalGuide} className="absolute top-4 right-4 text-3xs font-black uppercase tracking-widest text-emerald-200 hover:text-white">{t('add_spot.hide')}</button>
              </div>
            )}

            <div className="space-y-3">
              <div className="flex items-center justify-between ml-1">
                <label className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">{t('add_spot.category_label')}</label>
                <span className="text-3xs font-bold text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded-lg">
                  {t('add_spot.selected_categories_count', { count: selectedCategoryIds.length, defaultValue: '{{count}} valgt' })}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {categories.filter(cat => showAllCategories || visibleByDefaultIds.includes(cat.id) || selectedCategoryIds.includes(cat.id)).map((cat) => (
                  <button key={cat.id} type="button" onClick={() => handleCategorySelect(cat)} className={`relative flex items-center gap-2.5 p-3 rounded-2xl border transition-all ${selectedCategoryIds.includes(cat.id) ? 'bg-emerald-500/10 border-emerald-500 text-white shadow-lg' : 'bg-black/20 border-white/5 text-[var(--text-muted)] hover:bg-white/5'}`}>
                    <div className={`p-1.5 rounded-lg text-white text-sm ${selectedCategoryIds.includes(cat.id) ? cat.color : 'bg-[var(--bg-input)]'}`}>{cat.icon}</div>
                    <span className="text-2xs font-black uppercase tracking-tight truncate">{cat.label}</span>
                    {selectedCategoryId === cat.id && (
                      <span className="absolute -top-1.5 -right-1.5 text-[7px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-emerald-500 text-stone-950 border border-white/70">
                        {t('add_spot.primary_category', 'Primær')}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <button type="button" onClick={() => setShowAllCategories(v => !v)} className="flex items-center gap-1.5 text-2xs font-bold text-emerald-500 hover:text-emerald-400">
                {showAllCategories ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                {showAllCategories ? t('add_spot.fewer_categories') : t('add_spot.more_categories')}
              </button>
            </div>

            {selectedCategoryId === 'commercial_campsite' && !onlyPrivateWaypoint && (
              <div className="space-y-3 bg-amber-500/5 border border-amber-500/20 rounded-2xl p-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-3xs font-black text-amber-400 uppercase tracking-widest">{t('add_spot.campsite.section_title', 'Campingplass-informasjon')}</p>
                  <button
                    type="button"
                    onClick={() => triggerCampsiteEnrichment(false)}
                    disabled={campsiteEnrichLoading || !name.trim()}
                    title={t('add_spot.campsite.enrich_hint', 'Henter telefon, åpningstider og beskrivelse fra Google. Rediger fritt etterpå.')}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-400 text-2xs font-black uppercase tracking-widest transition-all hover:bg-amber-500/20 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {campsiteEnrichLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                    {t('add_spot.campsite.enrich_button', 'Hent info')}
                  </button>
                </div>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest ml-1">
                    {t('add_spot.campsite.phone_label', 'Telefon')}
                    {campsiteAiFields.has('phone') && <span className="flex items-center gap-1 text-amber-400 normal-case tracking-normal"><Sparkles size={9} /> {t('add_spot.campsite.ai_suggestion_tag', 'AI-forslag')}</span>}
                  </label>
                  <input type="tel" placeholder="+47 123 45 678" value={campsitePhone} onChange={(e) => { setCampsitePhone(e.target.value); clearAiField('phone'); }} className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl py-3 px-4 text-sm focus:border-amber-500 text-[var(--text-primary)] shadow-inner" />
                </div>
                <div className="space-y-2">
                  <label className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest ml-1">{t('add_spot.campsite.fee_label', 'Prisinformasjon')}</label>
                  <input type="text" placeholder={t('add_spot.campsite.fee_placeholder', 'F.eks. 350 kr/natt inkl. strøm')} value={campsiteFee} onChange={(e) => setCampsiteFee(e.target.value)} className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl py-3 px-4 text-sm focus:border-amber-500 text-[var(--text-primary)] shadow-inner" />
                </div>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest ml-1">
                    {t('add_spot.campsite.opening_hours_label', 'Åpningstider')}
                    {campsiteAiFields.has('opening_hours') && <span className="flex items-center gap-1 text-amber-400 normal-case tracking-normal"><Sparkles size={9} /> {t('add_spot.campsite.ai_suggestion_tag', 'AI-forslag')}</span>}
                  </label>
                  <input type="text" placeholder={t('add_spot.campsite.opening_hours_placeholder', 'F.eks. Hele året, 08:00-22:00')} value={campsiteOpeningHours} onChange={(e) => { setCampsiteOpeningHours(e.target.value); clearAiField('opening_hours'); }} className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl py-3 px-4 text-sm focus:border-amber-500 text-[var(--text-primary)] shadow-inner" />
                </div>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest ml-1">
                    <LinkIcon size={10} /> {t('add_spot.website_label', 'Nettside')}
                    {campsiteAiFields.has('website') && <span className="flex items-center gap-1 text-amber-400 normal-case tracking-normal"><Sparkles size={9} /> {t('add_spot.campsite.ai_suggestion_tag', 'AI-forslag')}</span>}
                  </label>
                  <input
                    type="url"
                    inputMode="url"
                    placeholder={t('add_spot.website_placeholder', 'https://...')}
                    value={website}
                    onChange={(e) => { setWebsite(e.target.value); clearAiField('website'); }}
                    className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl py-3 px-4 text-sm focus:border-amber-500 text-[var(--text-primary)] shadow-inner"
                  />
                  {hasWebsite && !isWebsiteValid && (
                    <p className="text-2xs text-red-400 font-bold">{t('add_spot.link_invalid', 'Legg inn en gyldig lenke (http/https).')}</p>
                  )}
                </div>
              </div>
            )}

            {/* Tilleggsfasiliteter / Egenskaper (velg flere) */}
            <div className="space-y-3 pt-2 border-t border-[var(--border-primary)]">
              <div className="flex items-center justify-between ml-1">
                <label className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">
                  {t('add_spot.additional_features_label', 'Tilleggsfasiliteter / Egenskaper (velg flere)')}
                </label>
                <span className="text-3xs font-bold text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded-lg">
                  {selectedAdditionalFeatures.length} valgt
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {categories
                  .filter(cat => !selectedCategoryIds.includes(cat.id) && ['teltplass', 'toalett', 'balplass', 'badeplass', 'vann', 'picnic', 'utsikt', 'gapahuk', 'kai', 'commercial_campsite'].includes(cat.id))
                  .map(cat => {
                    const isSelected = selectedAdditionalFeatures.includes(cat.feature);
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => {
                          setSelectedAdditionalFeatures(prev =>
                            isSelected ? prev.filter(f => f !== cat.feature) : [...prev, cat.feature]
                          );
                        }}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-tiny font-black transition-all ${
                          isSelected
                            ? 'bg-emerald-500/15 border-emerald-500 text-emerald-400 shadow-sm'
                            : 'bg-[var(--bg-input)] border-[var(--border-secondary)] text-[var(--text-muted)] hover:border-white/20 hover:text-[var(--text-primary)]'
                        }`}
                      >
                        <span className={isSelected ? 'text-emerald-400' : 'text-[var(--text-muted)]'}>{cat.icon}</span>
                        <span>{cat.label}</span>
                      </button>
                    );
                  })}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2 ml-1">
                <label className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">{t('add_spot.name_label')}</label>
                {!isSuggestionMode && !shouldLockSpotMetadata && previews.length === 0 && allowAiSuggestion && (
                  <button
                    type="button"
                    onClick={triggerContextAiSuggestion}
                    disabled={aiContextLoading}
                    className="flex items-center gap-1.5 text-2xs font-bold text-emerald-500 hover:text-emerald-400 disabled:opacity-50"
                  >
                    {aiContextLoading
                      ? <Loader2 size={12} className="animate-spin" />
                      : <Sparkles size={12} />}
                    {aiContextLoading
                      ? t('add_spot.ai_context_loading', 'Henter omgivelser…')
                      : t('add_spot.ai_context_button', 'Foreslå med AI')}
                  </button>
                )}
              </div>
              <input
                type="text"
                placeholder={t('add_spot.name_placeholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => { if (isCampingSpot && !onlyPrivateWaypoint) triggerCampsiteEnrichment(true); }}
                className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-2xl py-4 px-5 text-sm focus:border-emerald-500 text-[var(--text-primary)] shadow-inner"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest ml-1">{t('add_spot.photos_label')}</label>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={async () => {
                    if (Capacitor.isNativePlatform()) {
                      try {
                        const result = await pickPhoto();
                        if (result) {
                          const [compressed] = await compressImages([result.file]);
                          setSelectedFiles(prev => [...prev, compressed]);
                          setPreviews(prev => [...prev, URL.createObjectURL(compressed)]);
                          if (result.coords && !exifCoords && !isEditMode) {
                            setExifCoords(result.coords);
                            setAdjustedCoords(result.coords);
                          } else if (result.coords && isEditMode && initialData?.location) {
                            const existing = initialData.location;
                            const dLat = (result.coords.latitude - existing.latitude) * 111320;
                            const dLon = (result.coords.longitude - existing.longitude) * 111320 * Math.cos(existing.latitude * Math.PI / 180);
                            const distM = Math.sqrt(dLat * dLat + dLon * dLon);
                            if (distM > 50) {
                              toast(
                                t('add_spot.photo_coords_differ', 'Bildet har andre GPS-koordinater ({{dist}} m unna). Plassen beholder sin opprinnelige posisjon.', { dist: Math.round(distM) }),
                                'info'
                              );
                            }
                          }
                          if (!aiSuggested && !aiAnalyzing) triggerAiAnalysis(result.file);
                        }
                      } catch (e) {
                        console.error('[AddSpot] Photo pick/compress failed:', e);
                        toast(t('add_spot.alerts.photo_pick_failed', 'Kunne ikke hente bildet. Prøv et annet bilde eller ta et nytt med kamera.'), 'error');
                      }
                    } else fileInputRef.current?.click();
                  }}
                  className="w-20 h-20 bg-[var(--bg-input)] border-2 border-dashed border-[var(--border-secondary)] rounded-2xl flex flex-col items-center justify-center gap-1 text-[var(--text-muted)] hover:text-emerald-500 hover:border-emerald-500/40 group"
                >
                  <Camera size={20} className="group-hover:scale-110 transition-transform" />
                  <span className="text-[7px] font-black uppercase">{t('add_spot.add_photo')}</span>
                </button>
                <input type="file" multiple className="hidden" ref={fileInputRef} onChange={handleFileChange} />
                <div className="flex-1 flex gap-3 overflow-x-auto pb-2 no-scrollbar">
                  {previews.map((p, i) => (
                    <div key={i} className="relative shrink-0 w-20 h-20 rounded-2xl overflow-hidden border border-white/5 group">
                      <img src={p} className="w-full h-full object-cover" alt="" />
                      <button type="button" onClick={() => removeFile(i)} className="absolute top-1 right-1 p-1.5 bg-black/60 text-white rounded-lg opacity-100 transition-opacity"><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              </div>
              {aiAnalyzing && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-blue-500/10 border border-blue-500/20">
                  <Loader2 size={14} className="animate-spin text-blue-400" />
                  <span className="text-2xs text-blue-300 font-bold">{t('add_spot.ai_analyzing')}</span>
                </div>
              )}
            </div>

            {/* Map Interaction Model */}
            {(exifCoords || (coords && !isEditMode)) && (
              <div className="space-y-2">
                <label className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest ml-1 flex items-center gap-2">
                  <Navigation size={12} className={exifCoords ? "text-emerald-400" : "text-blue-400"} />
                  {exifCoords ? t('add_spot.exif_location_label') : t('add_spot.location_adjustment_label')}
                </label>
                <div className={`relative rounded-2xl overflow-hidden border shadow-lg ${exifCoords ? 'border-emerald-500/30' : 'border-blue-500/30'}`} style={{ height: 200 }}>
                  <MapContainer
                    center={[adjustedCoords?.latitude || coords.latitude, adjustedCoords?.longitude || coords.longitude]}
                    zoom={17}
                    zoomControl={false}
                    attributionControl={false}
                    style={{ width: '100%', height: '100%', zIndex: 1 }}
                  >
                    <ExifMapInvalidator />
                    <MapCenterTracker onCenterChange={setAdjustedCoords} />
                    <TileLayer url={SATELLITE_TILE_URL} maxZoom={19} />
                    <Marker position={[adjustedCoords?.latitude || coords.latitude, adjustedCoords?.longitude || coords.longitude]} icon={exifPinIcon} />
                  </MapContainer>

                  {/* Visual Fixed Center Pin Overlay */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full pointer-events-none z-[1001] mb-px">
                     <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill={exifCoords ? BRAND : "#3b82f6"} stroke="white" strokeWidth="2" className="drop-shadow-lg animate-bounce-subtle">
                        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3" fill="white"/>
                     </svg>
                  </div>

                  <div className="absolute top-2 right-2 z-[1000] flex flex-col gap-2">
                    <div className="bg-black/60 backdrop-blur-sm rounded-lg px-2 py-1 flex items-center gap-1.5 border border-white/10 shadow-lg">
                      <span className="text-3xs text-white/90 font-black uppercase tracking-widest">{t('add_spot.pan_hint')}</span>
                    </div>
                    {adjustedCoords && (Math.abs(adjustedCoords.latitude - (exifCoords?.latitude || coords.latitude)) > 0.000001) && (
                      <button type="button" onClick={() => setAdjustedCoords(exifCoords || null)} className="bg-red-500/80 hover:bg-red-500 text-white rounded-lg px-2 py-1 text-3xs font-black uppercase shadow-lg">
                        {t('add_spot.reset_location')}
                      </button>
                    )}
                  </div>
                  <div className="absolute bottom-2 left-2 z-[1000] bg-black/60 backdrop-blur-sm rounded-lg px-2 py-1 border border-white/10 shadow-lg">
                    <span className="text-3xs text-white/90 font-mono">{(adjustedCoords?.latitude || coords.latitude).toFixed(6)}, {(adjustedCoords?.longitude || coords.longitude).toFixed(6)}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest ml-1">{t('add_spot.description_label')}</label>
              <textarea placeholder={t('add_spot.description_placeholder')} value={description} onChange={(e) => setDescription(e.target.value)} className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-2xl py-4 px-5 text-sm focus:border-emerald-500 text-[var(--text-primary)] min-h-[100px] shadow-inner" required />
            </div>

            {!shouldLockSpotMetadata && !onlyPrivateWaypoint && (
              <>
                <div className="space-y-2">
                  {!(isAdminEditor && isCampingSpot) && (
                    <button type="button" onClick={() => setShowLinks(v => !v)} className="flex items-center gap-1.5 text-2xs font-bold text-emerald-500 hover:text-emerald-400">
                      {showLinks ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      <LinkIcon size={12} />
                      {showLinks ? t('add_spot.fewer_links', 'Skjul lenker') : t('add_spot.more_links', 'Legg til lenke / video')}
                    </button>
                  )}
                  {showLinks && (
                    <div className="space-y-3 pt-1">
                      {selectedCategoryId !== 'commercial_campsite' && (
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest ml-1">
                            <LinkIcon size={10} /> {t('add_spot.website_label', 'Nettside (valgfritt)')}
                          </label>
                          <input
                            type="url"
                            inputMode="url"
                            placeholder={t('add_spot.website_placeholder', 'https://...')}
                            value={website}
                            onChange={(e) => setWebsite(e.target.value)}
                            className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-2xl py-3 pl-10 pr-4 text-sm focus:border-emerald-500 text-[var(--text-primary)] shadow-inner"
                          />
                          {hasWebsite && !isWebsiteValid && (
                            <p className="text-2xs text-red-400 font-bold">{t('add_spot.link_invalid', 'Legg inn en gyldig lenke (http/https).')}</p>
                          )}
                        </div>
                      )}

                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest ml-1">
                          <LinkIcon size={10} /> {t('add_spot.video_link_label')}
                        </label>
                        <input
                          type="url"
                          inputMode="url"
                          placeholder={t('add_spot.video_link_placeholder')}
                          value={videoUrl}
                          onChange={(e) => setVideoUrl(e.target.value)}
                          className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-2xl py-3 px-5 text-sm focus:border-emerald-500 text-[var(--text-primary)] shadow-inner"
                        />
                        {hasVideoUrl && !isVideoUrlValid && (
                          <p className="text-2xs text-red-400 font-bold">{t('add_spot.video_link_invalid')}</p>
                        )}
                        {videoThumbnail && (
                          <img src={videoThumbnail} alt="" className="mt-2 w-full h-28 object-cover rounded-xl border border-white/10" loading="lazy" />
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Synlighets-velger fjernet: i ny-modus styrer "Del med fellesskapet"-
                    toggelen lenger ned synligheten. I edit-modus bevares opprinnelig
                    `visibility` automatisk via `isPrivate`-state. */}
              </>
            )}

            {selectedCategoryId === 'attraksjon' && !onlyPrivateWaypoint && (
              <div className="space-y-3 bg-violet-500/5 border border-violet-500/20 rounded-2xl p-4">
                <p className="text-3xs font-black text-violet-400 uppercase tracking-widest">{t('add_spot.attraction.section_title')}</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: 'viewpoint', label: t('discover.viewpoint'), icon: 'fa-solid fa-eye' },
                    { key: 'waterfall', label: t('discover.waterfall'), icon: 'fa-solid fa-water' },
                    { key: 'museum', label: t('discover.museum'), icon: 'fa-solid fa-landmark' },
                    { key: 'monument', label: t('discover.monument'), icon: 'fa-solid fa-monument' },
                    { key: 'castle', label: t('discover.castle'), icon: 'fa-solid fa-chess-rook' },
                    { key: 'peak', label: t('discover.peak'), icon: 'fa-solid fa-mountain' },
                    { key: 'attraction', label: t('discover.attraction'), icon: 'fa-solid fa-star' },
                  ].map(sub => (
                    <button key={sub.key} type="button" onClick={() => setAttractionSubCategory(sub.key)} className={`flex flex-col items-center gap-1 p-2 rounded-xl border transition-all ${attractionSubCategory === sub.key ? 'bg-violet-500/15 border-violet-500 text-white' : 'bg-black/20 border-white/5 text-[var(--text-muted)]'}`}>
                      <i className={`${sub.icon} text-xs`} />
                      <span className="text-4xs font-bold truncate w-full text-center">{sub.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {showSharingToggles && (
              <div className="space-y-3 rounded-2xl border border-white/10 bg-black/20 p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shareWithCommunity}
                    onChange={(e) => setShareWithCommunity(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-emerald-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-tiny font-black text-[var(--text-primary)] uppercase tracking-wide">
                      <i className="fa-solid fa-earth-europe text-emerald-500" />
                      {t('add_spot.share_with_community', 'Del med fellesskapet')}
                    </div>
                    <p className="text-2xs text-[var(--text-muted)] leading-snug mt-0.5">
                      {shareWithCommunity
                        ? t('add_spot.share_on_hint', 'Andre Fricamp-brukere ser plassen på kartet.')
                        : t('add_spot.share_off_hint', 'Plassen blir kun et privat punkt — ingen andre ser den.')}
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={saveToMyContent}
                    onChange={(e) => setSaveToMyContent(e.target.checked)}
                    disabled={!shareWithCommunity}
                    className="mt-0.5 w-4 h-4 accent-emerald-500 disabled:opacity-50"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-tiny font-black text-[var(--text-primary)] uppercase tracking-wide">
                      <i className="fa-solid fa-folder text-amber-400" />
                      {t('add_spot.save_to_my_content', 'Lagre i Mitt innhold')}
                    </div>
                    <p className="text-2xs text-[var(--text-muted)] leading-snug mt-0.5">
                      {!shareWithCommunity
                        ? t('add_spot.save_required_hint', 'Påkrevd når deling er av — punktet må lagres et sted.')
                        : t('add_spot.save_on_hint', 'Du finner punktet igjen i din personlige liste.')}
                    </p>
                  </div>
                </label>

                {shareWithCommunity && (() => {
                  const isLoading = nearestBuildingM === undefined;
                  const tooClose = typeof nearestBuildingM === 'number' && nearestBuildingM < 150 && nearestBuildingM > 0;
                  const tone = tooClose
                    ? 'bg-amber-500/10 border-amber-500/30 text-amber-200'
                    : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-200';
                  const iconTone = tooClose ? 'text-amber-400' : 'text-emerald-400';
                  const message = isLoading
                    ? t('add_spot.measuring_buildings', 'Måler avstand til nærmeste bygning ...')
                    : tooClose
                      ? t('add_spot.share_150m_too_close_warning',
                          'OBS: Plassen er ca. {{dist}}m fra nærmeste bygning. Vis ekstra hensyn til privat eiendom!',
                          { dist: nearestBuildingM })
                      : typeof nearestBuildingM === 'number' && nearestBuildingM > 0
                        ? t('add_spot.share_150m_ok',
                            'Avstand til nærmeste bygning: ca. {{dist}}m. Plassen kan deles.',
                            { dist: nearestBuildingM })
                        : t('add_spot.share_150m_warning', 'Vis alltid hensyn til privat eiendom.');
                  return (
                    <div className={`flex items-start gap-2 rounded-xl border px-3 py-2 ${tone}`}>
                      {isLoading
                        ? <Loader2 size={12} className={`${iconTone} shrink-0 mt-0.5 animate-spin`} />
                        : <Info size={12} className={`${iconTone} shrink-0 mt-0.5`} />}
                      <p className="text-2xs leading-snug">{message}</p>
                    </div>
                  );
                })()}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting || !name.trim() || !description.trim() || !isVideoUrlValid || !isWebsiteValid || (showSharingToggles && !shareWithCommunity && !saveToMyContent)}
              className={`w-full py-5 rounded-2xl font-black text-xs uppercase tracking-widest shadow-2xl transition-all flex items-center justify-center gap-3 active:scale-95 disabled:opacity-50 ${isSuggestionMode ? 'bg-amber-600' : 'bg-emerald-600'}`}
            >
              {isSubmitting ? <Loader2 className="animate-spin" /> : isSuggestionMode ? <MailQuestion size={18} /> : isEditMode ? <Save size={18} /> : <Send size={18} />}
              {isSubmitting ? t('add_spot.submit.processing') : isSuggestionMode ? t('add_spot.submit.send_suggestion') : isEditMode ? t('add_spot.submit.save_changes') : t('add_spot.submit.publish')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddSpotModal;
